import axios from 'axios';
import i18next from 'i18next';
import { isEmpty, pick } from 'lodash';
import sha1 from 'sha1';

import config from '@/config';
import { fallbackLocalStorage } from '@/localStorageWrapper';
import router from '@/router';
import { resetStore } from '@/store';
import { executeRequest, formatRole, getApiUrl, getCarrierServiceApiUrl, getQuery } from '@/utils';

const state = {
  user: null,
  merchantConfiguration: null,
  accountSettings: {
    autopack: false,
    print_packing_slips: false,
    create_return_labels: false,
    customer_notifications_enabled: false,
    notifications_from_email: '',
    notifications_bcc_emails: []
  },
  featureFlags: {},
  initQuery: {},
  error: '',
  shopifySubscriptions: [],
  // 2FA/OTP state management
  loginData: null,
  temporaryPassword: null, // Store password in memory only, never persist
  otp: {
    show2FA: false,
    isVerifying: false,
    isResending: false,
    sessionTimeout: null
  }
};

const getters = {
  isAdmin(state) {
    return state.user?.admin;
  },
  isPacker(state) {
    return state.user?.dispatcher && !state.user?.driver;
  },
  isCustomService(state) {
    return state.user?.dispatcher && state.user?.driver;
  },
  showSubscriptionPage(state) {
    const isShopifyMode = state.initQuery?.platform === 'shopify';

    if (!isShopifyMode) {
      return false;
    }

    if (!state.shopifySubscriptions.length) {
      return true;
    }

    if (
      state.shopifySubscriptions.some((subscription) => {
        return subscription.status === 'active' && !!subscription?.billing_on;
      })
    ) {
      return false;
    }

    return true;
  },
  user(state) {
    return state.user;
  },
  merchantConfiguration(state) {
    return state.merchantConfiguration;
  },
  featureFlags(state) {
    return state.featureFlags;
  },
  apiUrl(state, getters) {
    return getApiUrl(getters.region);
  },
  carrierServiceApiUrl(state, getters) {
    return getCarrierServiceApiUrl(getters.region);
  },
  region(state) {
    return state.user?.region || localStorage.getItem('authentication_region');
  },
  token(state) {
    return state.user?.authentication_token || localStorage.getItem('authentication_token');
  },
  deliveryhubAccountKey(state) {
    return state.accountSettings?.deliveryhubAccountKey || localStorage.getItem('authentication_token');
  },
  accountSettings(state) {
    return state.accountSettings;
  },
  branding(state) {
    const settings = pick(state.accountSettings, [
      'company_name',
      'customer_support_email',
      'customer_support_phone',
      'customer_support_url',
      'customer_support_url_display_name',
      'notifications_from_email',
      'notifications_bcc_emails'
    ]);

    for (const key in settings) {
      if (settings[key] === null) {
        settings[key] = '';
      }
    }

    return settings;
  },
  isPackingSlipsPrintingEnabled(state) {
    return state.accountSettings.print_packing_slips;
  },
  // 2FA getters
  show2FA(state) {
    return state.otp.show2FA;
  },
  otpState(state) {
    return state.otp;
  },
  loginData(state) {
    return state.loginData;
  }
};

const actions = {
  async preInit({ commit }) {
    const query = getQuery();

    if (query.platform === 'shopify') {
      fallbackLocalStorage();

      if (query.userData) {
        commit('setUser', JSON.parse(query.userData));
        delete query.userData;
      }

      commit('setInitQuery', query);
    }
  },

  async init({ commit, dispatch, getters }, data) {
    let userData = data;

    if (isEmpty(userData) && !getters.token) {
      commit('clearOtpState');

      return;
    }

    if (isEmpty(userData)) {
      try {
        const response = await axios.get(`${getters.apiUrl}/tokens`, {
          ignoreLogout: true
        });

        userData = response.data;
      } catch (error) {
        if (error.response?.status === 401) {
          return dispatch('logout');
        }
      }
    }

    await i18next.changeLanguage(userData.language);

    commit('setUser', userData);
    commit('setFeatureFlags', userData.feature_flags);

    try {
      await dispatch('getAccountSettings');
    } catch (error) {
      dispatch('logout');
      throw error;
    }

    dispatch('carriers/loadCarriers', null, { root: true });

    dispatch('getSubscription');

    localStorage.setItem('authentication_token', userData.authentication_token);
    localStorage.setItem('authentication_region', userData.region);
    localStorage.setItem('deliveryhubAccountKey', getters.deliveryhubAccountKey);
    dispatch('setGtmData');
  },

  setGtmData({ state }) {
    const { user, accountSettings } = state;

    window.gtmData = {
      userId: user.id,
      userName: user.name,
      merchantId: user.merchant_id,
      merchantName: accountSettings.account_company_name,
      userRole: formatRole(user),
      userRegion: user.region,
      isBringgMerchant: user.merchant_id === 1,
      isBringgEmployee: user.email?.endsWith('@bringg.com'),
      isImpersonated:
        typeof localStorage !== 'undefined' && Boolean(localStorage.getItem('ls.impersonate_original_user'))
    };
  },

  async getAccountSettings({ commit, getters }) {
    const response = await axios.get(`${getters.apiUrl}/deliveryhub-orders-service/v1/accounts`, {
      headers: { Authorization: `Bearer ${getters.token}` }
    });

    commit('setAccountSettings', response.data);
  },

  async updateAccountSettings(options, data) {
    const request = {
      request: {
        method: 'PUT',
        url: `/deliveryhub-orders-service/v1/accounts/account_settings`,
        data
      },
      options,
      errorMessage: 'ACCOUNT_SETTINGS_UPDATING_FAIL'
    };

    await executeRequest(request, (response) => {
      options.commit('setAccountSettings', response.data);
    });
  },

  async getMerchantConfiguration({ getters, commit }) {
    const { data } = await axios.get(`${getters.apiUrl}/merchant/merchant_configuration`, {
      headers: { Authorization: `Bearer ${getters.token}` }
    });

    commit('setMerchantConfiguration', data);

    return data;
  },

  async login({ dispatch, commit }, data) {
    const url = getApiUrl(data.merchant?.region);

    try {
      const response = await axios.post(
        `${url}/tokens`,
        {
          remember_me: true,
          email: sha1(data.email.toLowerCase()),
          password: data.password,
          merchant_uuid: data.merchant?.uuid,
          recaptcha: data.recaptcha
        },
        {
          ignoreLogout: true
        }
      );

      if (response.data.merchants_data?.length === 1) {
        if (response.data.merchants_data[0].region !== 'eu-west-1-gcp') {
          return { data: { success: false, message: 'This account is not supported' } };
        }

        const merchant = response.data.merchants_data[0];

        return dispatch('login', { ...data, merchant });
      }

      // Check for OTP requirement
      if (response.data.otp_sent) {
        commit('setLoginData', {
          email: data.email,
          password: data.password,
          merchantUuid: data.merchant?.uuid,
          region: data.merchant?.region,
          expirationTime: response.data.expiration
        });

        // Store password temporarily in memory only for OTP verification
        commit('setTemporaryPassword', data.password);
        commit('setShow2FA', true);

        return response;
      }

      if (response.data.authentication_token) {
        await dispatch('init', response.data);
        await dispatch('postLogin', response.data);
        await router.push({ name: 'orders' });
      }

      return response;
    } catch (error) {
      // Handle OTP_REQUIRED exception
      if (error.response?.data?.reason?.name === 'OTP_REQUIRED') {
        commit('setLoginData', {
          email: data.email,
          merchantUuid: data.merchant?.uuid,
          region: data.merchant?.region,
          expirationTime: error.response.data.expiration
        });

        // Store password temporarily in memory only for OTP verification
        commit('setTemporaryPassword', data.password);
        commit('setShow2FA', true);

        return { data: { otp_required: true } };
      }

      throw error;
    }
  },

  async postLogin({ getters }, data) {
    const query = getQuery();

    if (query.platform === 'shopify') {
      await axios.put(`${getters.apiUrl}/deliveryhub-orders-service/v1/integrations`, {
        type: 1,
        shop: query.shop,
        credentials: {
          [query.id]: {
            authentication_token: data.authentication_token,
            region: data.region
          }
        }
      });
    }
  },

  async getSubscription({ getters, commit, state }) {
    if (state.initQuery.platform !== 'shopify') {
      return;
    }

    const subscriptions = await axios.get(
      `${getters.apiUrl}/deliveryhub-orders-service/v1/integrations/shopify/subscriptions?shop=${state.initQuery.shop}`
    );

    commit('setShopifySubscriptions', subscriptions.data);
  },

  async signup(_, data) {
    const response = await axios.post(`${getApiUrl(data)}/deliveryhub-orders-service/v1/accounts`, {
      email: data.email.toLowerCase(),
      password: data.password,
      name: data.name,
      passwordConfirmation: data.passwordConfirmation,
      companyName: data.companyName,
      shop: data.query?.shop
    });

    if (response.data.success) {
      await router.push({ name: 'login', query: data.query || {} });
    }

    return response;
  },

  async remind(_, data) {
    const url = getApiUrl();

    const response = await axios.post(`${url}/users/remind`, {
      email: data.email.toLowerCase(),
      recaptcha: data.recaptcha
    });

    return response;
  },

  async reset(_, data) {
    const url = getApiUrl(data.region);

    const response = await axios.post(`${url}/users/reset`, {
      email: data.email.toLowerCase(),
      password: data.password,
      token: data.token
    });

    return response;
  },

  async preLogout(options) {
    const query = options.state.initQuery;

    if (query.platform === 'shopify') {
      await executeRequest({
        request: {
          ignoreLogout: true,
          method: 'PUT',
          url: '/deliveryhub-orders-service/v1/integrations',
          data: {
            type: 1,
            shop: query.shop,
            credentials: {
              [query.id]: null
            }
          }
        },
        options
      });
    }

    if (localStorage.getItem('authentication_token')) {
      await executeRequest({
        request: {
          ignoreLogout: true,
          method: 'DELETE',
          url: '/tokens'
        },
        options
      });
    }
  },

  async logout(options) {
    options.commit('setUser', null);

    await options.dispatch('preLogout');

    localStorage.removeItem('authentication_token');
    localStorage.removeItem('authentication_region');
    localStorage.removeItem('deliveryhubAccountKey');

    await router.push({ name: 'login', query: options.state.initQuery });
    resetStore();
  },

  async updateUser(options, data) {
    await i18next.changeLanguage(data.language);

    options.commit('setUser', data);
  },

  // 2FA/OTP actions with gap fixes
  async loginWithOtp({ commit, dispatch, state }, { otpCode }) {
    commit('setOtpVerifying', true);

    try {
      const retryCount = config.get('OTP_CONFIG')?.RETRY_COUNT || 3;
      const retryDelay = config.get('OTP_CONFIG')?.RETRY_DELAY || 1000;
      let lastError = null;

      for (let attempt = 0; attempt < retryCount; attempt++) {
        try {
          const url = getApiUrl(state.loginData.region);
          const otpVerifyUrl = config.get('OTP_ENDPOINTS')?.VERIFY_URL || '/otp';

          const payload = {
            email: sha1(state.loginData.email.toLowerCase()),
            password: state.temporaryPassword,
            otp: otpCode,
            merchant_uuid: state.loginData.merchantUuid,
            region: state.loginData.region
          };

          // Don't include reCAPTCHA for OTP verification - backend doesn't require it

          const response = await axios.post(`${url}${otpVerifyUrl}`, payload, {
            ignoreLogout: true,
            timeout: 10000
          });

          if (response.data.authentication_token) {
            commit('resetOtpState');
            await dispatch('init', response.data);
            await dispatch('postLogin', response.data);
            await router.push({ name: 'orders' });

            return { success: true };
          }

          return response;
        } catch (error) {
          lastError = error;

          // Don't retry for authentication errors or recaptcha required
          if (
            error.response?.status === 401 ||
            error.response?.status === 423 ||
            error.response?.data?.recaptcha_stoken
          ) {
            break;
          }

          if (attempt === retryCount - 1) {
            break;
          }

          // Wait before retry
          await new Promise((resolve) => setTimeout(resolve, retryDelay * Math.pow(2, attempt)));
        }
      }

      // Handle final error after all retries
      if (lastError?.response?.status === 423) {
        return { success: false, showCaptcha: true };
      }

      // Check if recaptcha is required (backend sends recaptcha_stoken)
      if (lastError?.response?.data?.recaptcha_stoken) {
        return {
          success: false,
          showCaptcha: true,
          message: lastError?.response?.data?.message || 'Please complete the reCAPTCHA verification.'
        };
      }

      if (lastError?.code === 'ECONNABORTED' || lastError?.code === 'ERR_NETWORK') {
        return { success: false, message: 'Network error. Please check your connection and try again.' };
      }

      return {
        success: false,
        message: lastError?.response?.data?.message || 'Invalid OTP code. Please try again.'
      };
    } finally {
      commit('setOtpVerifying', false);
    }
  },

  async sendOtpEmail({ commit, state }) {
    commit('setOtpResending', true);

    try {
      const retryCount = config.get('OTP_CONFIG')?.RETRY_COUNT || 3;
      const retryDelay = config.get('OTP_CONFIG')?.RETRY_DELAY || 1000;
      let lastError = null;

      for (let attempt = 0; attempt < retryCount; attempt++) {
        try {
          const url = getApiUrl(state.loginData.region);
          const otpSendUrl = config.get('OTP_ENDPOINTS')?.SEND_URL || '/otp/send';

          const response = await axios.post(
            `${url}${otpSendUrl}`,
            {
              email: state.loginData.email,
              protected_action: 'Login'
            },
            {
              ignoreLogout: true,
              timeout: 10000
            }
          );

          // Backend returns expiration directly, not a success field
          if (response.data && response.data.expiration) {
            commit('updateLoginDataExpiration', response.data.expiration);

            return { success: true, expiration: response.data.expiration };
          }

          // If we got a 200 response but no expiration, still consider it successful
          if (response.status === 200) {
            return { success: true };
          }

          return { success: false, message: 'Unexpected response format' };
        } catch (error) {
          lastError = error;

          if (attempt === retryCount - 1) {
            break;
          }

          await new Promise((resolve) => setTimeout(resolve, retryDelay * Math.pow(2, attempt)));
        }
      }

      if (lastError?.code === 'ECONNABORTED' || lastError?.code === 'ERR_NETWORK') {
        return { success: false, message: 'Network error. Please check your connection and try again.' };
      }

      return { success: false, message: lastError?.response?.data?.message || 'Failed to send OTP. Please try again.' };
    } finally {
      commit('setOtpResending', false);
    }
  },

  async resetOtpState({ commit }) {
    commit('resetOtpState');
  }
};

const mutations = {
  setUser(state, user) {
    state.user = user;
  },

  setFeatureFlags(state, featureFlags) {
    state.featureFlags = featureFlags;
  },

  setAccountSettings(state, accountSettings) {
    state.accountSettings = { ...state.accountSettings, ...accountSettings };
  },

  setMerchantConfiguration(state, merchantConfiguration) {
    state.merchantConfiguration = merchantConfiguration;
  },

  setInitQuery(state, query) {
    state.initQuery = query;
  },
  setShopifySubscriptions(state, data) {
    state.shopifySubscriptions = data;
  },

  clearError(state) {
    state.error = '';
  },

  // 2FA/OTP mutations with gap fixes
  setLoginData(state, loginData) {
    state.loginData = loginData;
    // Persist to sessionStorage for browser refresh recovery
    if (loginData) {
      sessionStorage.setItem('bringg-2fa-loginData', JSON.stringify(loginData));
    }
  },

  setShow2FA(state, show) {
    state.otp.show2FA = show;
    // Persist 2FA state for browser refresh recovery
    sessionStorage.setItem('bringg-2fa-active', show.toString());
  },

  setTemporaryPassword(state, password) {
    state.temporaryPassword = password;
  },

  setOtpVerifying(state, verifying) {
    state.otp.isVerifying = verifying;
  },

  setOtpResending(state, resending) {
    state.otp.isResending = resending;
  },

  setOtpSessionTimeout(state, timeoutId) {
    state.otp.sessionTimeout = timeoutId;
  },

  clearOtpSessionTimeout(state) {
    if (state.otp.sessionTimeout) {
      clearTimeout(state.otp.sessionTimeout);
      state.otp.sessionTimeout = null;
    }
  },

  updateLoginDataExpiration(state, expiration) {
    if (state.loginData) {
      state.loginData.expirationTime = expiration;
    }
  },

  resetOtpState(state) {
    // Clear session timeout for security
    if (state.otp.sessionTimeout) {
      clearTimeout(state.otp.sessionTimeout);
    }

    // Clear sessionStorage persistence
    sessionStorage.removeItem('bringg-2fa-loginData');
    sessionStorage.removeItem('bringg-2fa-active');

    state.loginData = null;
    state.otp = {
      show2FA: false,
      isVerifying: false,
      isResending: false,
      sessionTimeout: null
    };
  },

  clearOtpState(state) {
    sessionStorage.removeItem('bringg-2fa-loginData');
    sessionStorage.removeItem('bringg-2fa-active');
    state.temporaryPassword = null;
    state.loginData = null;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
